import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../components/layouts/MainLayout';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { DataTable } from '../components/ui/data-table';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { MultiSelect } from '../components/ui/multi-select';
import { UserCombobox } from '../components/ui/user-combobox';
import { offboardingAPI, usersAPI, applicationsAPI } from '../lib/firestore';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/ui/use-toast';
import type { OffboardingRequest, User, Application } from '../types';
import { formatDate } from '../lib/utils';

export const OffboardingPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [offboardings, setOffboardings] = useState<OffboardingRequest[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [selectedEmployeeEmail, setSelectedEmployeeEmail] = useState('');
  const [selectedAppIds, setSelectedAppIds] = useState<string[]>([]);

  useEffect(() => {
    loadOffboardings();
    loadUsers();
    loadApplications();
  }, []);

  const loadOffboardings = async () => {
    try {
      setLoading(true);
      const data = await offboardingAPI.getAll();
      setOffboardings(data);
    } catch (error) {
      console.error('Error loading offboardings:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const data = await usersAPI.getActiveAndSuspended();
      setUsers(data);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadApplications = async () => {
    try {
      const data = await applicationsAPI.getAll();
      // Filter: active, local auth (login/password), and internet accessible
      const filtered = data.filter(
        (app) =>
          app.status === 'active' &&
          app.authType === 'local' &&
          app.internetAccessible === true
      );
      setApplications(filtered);
    } catch (error) {
      console.error('Error loading applications:', error);
    }
  };

  const handleCreateOffboarding = async () => {
    if (!selectedEmployeeEmail || selectedAppIds.length === 0) {
      toast({
        title: 'Error',
        description: 'Please select an employee and at least one application',
        className: 'bg-red-50 border-red-200',
      });
      return;
    }

    try {
      const employee = users.find((u) => u.email === selectedEmployeeEmail);
      if (!employee) return;

      const offboardingApplications = selectedAppIds
        .map((appId) => {
          const app = applications.find((a) => a.id === appId);
          if (!app) return null;

          const confirmationStatus: any = {};
          app.admins.forEach((adminEmail) => {
            confirmationStatus[adminEmail] = {
              status: 'pending',
            };
          });

          return {
            appId: app.id,
            appName: app.name,
            adminEmails: app.admins,
            confirmationStatus,
          };
        })
        .filter((app): app is NonNullable<typeof app> => app !== null);

      await offboardingAPI.create({
        employeeEmail: employee.email,
        employeeName: employee.fullName,
        initiatedBy: user?.email || '',
        status: 'in_progress',
        applications: offboardingApplications,
      });

      setIsCreateDialogOpen(false);
      setSelectedEmployeeEmail('');
      setSelectedAppIds([]);
      await loadOffboardings();

      toast({
        title: 'Success',
        description: 'Offboarding process created successfully',
        className: 'bg-green-50 border-green-200',
      });
    } catch (error) {
      console.error('Error creating offboarding:', error);
      toast({
        title: 'Error',
        description: 'Failed to create offboarding process',
        className: 'bg-red-50 border-red-200',
      });
    }
  };

  const calculateProgress = (offboarding: OffboardingRequest): number => {
    if (offboarding.applications.length === 0) return 0;

    let totalItems = 0;
    let completedItems = 0;

    offboarding.applications.forEach((app) => {
      // Only count apps with admins assigned
      if (app.adminEmails.length > 0) {
        app.adminEmails.forEach((email) => {
          totalItems++;
          if (
            app.confirmationStatus[email]?.status &&
            app.confirmationStatus[email].status !== 'pending'
          ) {
            completedItems++;
          }
        });
      }
    });

    if (totalItems === 0) return 0;
    return Math.round((completedItems / totalItems) * 100);
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">Offboarding Processes</h1>
          <Button onClick={() => setIsCreateDialogOpen(true)}>Create New Offboarding</Button>
        </div>

{loading ? (
          <div>Loading offboardings...</div>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>All Processes ({offboardings.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <DataTable
                data={offboardings}
                columns={[
                  {
                    key: 'employeeName',
                    label: 'Employee',
                    sortable: true,
                    render: (offboarding) => <span className="font-medium">{offboarding.employeeName}</span>,
                  },
                  {
                    key: 'employeeEmail',
                    label: 'Email',
                    sortable: true,
                  },
                  {
                    key: 'createdAt',
                    label: 'Created',
                    sortable: true,
                    render: (offboarding) => formatDate(offboarding.createdAt),
                  },
                  {
                    key: 'status',
                    label: 'Status',
                    sortable: true,
                    render: (offboarding) => (
                      <Badge
                        className={
                          offboarding.status === 'completed'
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 hover:bg-green-100 dark:hover:bg-green-900/30'
                            : offboarding.status === 'in_progress'
                            ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-200 hover:bg-indigo-100 dark:hover:bg-indigo-900/30'
                            : 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800'
                        }
                      >
                        {offboarding.status.replace('_', ' ')}
                      </Badge>
                    ),
                  },
                  {
                    key: 'unassigned',
                    label: 'Unassigned',
                    render: (offboarding) => {
                      const unassignedCount = offboarding.applications.filter(
                        (app) => app.adminEmails.length === 0
                      ).length;

                      if (unassignedCount === 0) {
                        return <span className="text-sm text-muted-foreground">-</span>;
                      }

                      return (
                        <Badge className="bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 hover:bg-yellow-100 dark:hover:bg-yellow-900/30">
                          {unassignedCount} app{unassignedCount > 1 ? 's' : ''}
                        </Badge>
                      );
                    },
                  },
                  {
                    key: 'progress',
                    label: 'Progress',
                    render: (offboarding) => {
                      const progress = calculateProgress(offboarding);
                      return (
                        <div className="flex items-center space-x-2">
                          <div className="w-24 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                            <div
                              className="bg-indigo-600 dark:bg-indigo-500 h-2 rounded-full"
                              style={{ width: `${progress}%` }}
                            />
                          </div>
                          <span className="text-sm">{progress}%</span>
                        </div>
                      );
                    },
                  },
                  {
                    key: 'actions',
                    label: 'Actions',
                    render: (offboarding) => (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/offboarding/${offboarding.id}`)}
                      >
                        View Details
                      </Button>
                    ),
                  },
                ]}
                searchKeys={['employeeName', 'employeeEmail']}
                filters={[
                  {
                    key: 'status',
                    label: 'Status',
                    options: [
                      { value: 'pending', label: 'Pending' },
                      { value: 'in_progress', label: 'In Progress' },
                      { value: 'completed', label: 'Completed' },
                      { value: 'archived', label: 'Archived' },
                    ],
                  },
                ]}
                emptyMessage="No offboarding processes found. Create a new one to get started."
                pageSize={25}
              />
            </CardContent>
          </Card>
        )}
      </div>

      {/* Create Offboarding Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Offboarding</DialogTitle>
            <DialogDescription>
              Select the employee and applications to start the offboarding process.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="employee">Employee *</Label>
              <UserCombobox
                users={users}
                value={selectedEmployeeEmail}
                onValueChange={setSelectedEmployeeEmail}
                placeholder="Search and select employee..."
              />
            </div>
            <div className="grid gap-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="applications">Applications * (Login/Password, Internet accessible)</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedAppIds(applications.map((app) => app.id))}
                >
                  Select All ({applications.length})
                </Button>
              </div>
              <MultiSelect
                options={applications.map((app) => ({
                  value: app.id,
                  label: app.name,
                }))}
                selected={selectedAppIds}
                onChange={setSelectedAppIds}
                placeholder="Select applications..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateOffboarding}>Create Offboarding</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};
